import React from 'react';
import clsx from 'clsx';
import { Dropdown, Button, colors } from '@insurancemenu/imenu-components-lib';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Dropdown';
import {
  ChevronDown,
  Delete,
  Download,
  IconCheckmark,
  WarningFilled,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { Step, Upload } from 'types/carrierFileService';
import { capitalize } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { observer } from 'mobx-react-lite';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { createStyles } from './styles';

export interface MoreDropdownProps {
  fileUpload: Upload;
  view: 'Links' | 'Action';
}

const MoreDropdown = observer(({ fileUpload, view }: MoreDropdownProps): JSX.Element => {
  const classes = createStyles();
  const { id, processingOutput, step } = fileUpload;
  const isActionView = view === 'Action';
  const currentDate = new Date().toISOString().slice(0, 10);

  const {
    uploadSyncFilesStore: {
      sponsorId,
      date: { from: dateFrom },
      updateDate,
      openWarningModal,
      validateUpload,
      getFileUploads,
      processUpload,
      getOutputDownloadUrl,
    },
  } = useRootStore();

  const convertLinkName = (stringToConvert: string): string => {
    const words = stringToConvert.split(/(?=[A-Z])/); // Split into words at capital letters
    const wordsWithoutFile = words.filter((word) => !word.match(/path/i)); // Remove words that contain 'path'
    const capitalizedWords = wordsWithoutFile.map((word) => capitalize(word));
    return capitalizedWords.join(' ');
  };

  const isValidateOptionDisabled = step === Step.DONE_FULLY;
  const isProcessOptionDisabled = step !== Step.VALIDATION_DONE;

  const getOptionStyles = (disabledStatus: boolean): string => {
    return disabledStatus ? clsx(classes.dropdownOption, classes.disabledOption) : classes.dropdownOption;
  };

  const getButtonStyles = (disabledStatus: boolean): string => {
    return disabledStatus ? classes.disabledButton : '';
  };

  const onFileAction = async (fileFunc: (id: string) => Promise<void>): Promise<void> => {
    await fileFunc(id);

    // Call getFileUploads the first time
    void getFileUploads(dateFrom, currentDate, sponsorId);
    updateDate(dateFrom, currentDate);

    // Wait for 2 seconds and call getFileUploads again
    setTimeout(() => {
      void getFileUploads(dateFrom, currentDate, sponsorId);
    }, 2000);
  };

  const dropdownOptions: DropdownOptionType[] = [
    {
      value: 'validateUpload',
      onOptionClick: isValidateOptionDisabled
        ? undefined
        : async (): Promise<void> => {
            await onFileAction(validateUpload);
          },
      label: (
        <Button
          type="text"
          leftIconProp={<WarningFilled />}
          disabled={isValidateOptionDisabled}
          className={getButtonStyles(isValidateOptionDisabled)}
        >
          Validate File Upload
        </Button>
      ),
      className: getOptionStyles(isValidateOptionDisabled),
    },
    {
      value: 'processUpload',
      onOptionClick: isProcessOptionDisabled
        ? undefined
        : async (): Promise<void> => {
            await onFileAction(processUpload);
          },
      label: (
        <Button
          type="text"
          leftIconProp={<IconCheckmark />}
          disabled={isProcessOptionDisabled}
          className={getButtonStyles(isProcessOptionDisabled)}
        >
          Process File Upload
        </Button>
      ),
      className: getOptionStyles(isProcessOptionDisabled),
    },
    {
      value: 'removeUpload',
      onOptionClick: (): void => {
        openWarningModal(id);
      },
      label: (
        <Button type="text" leftIconProp={<Delete />} color="custom" customColor={colors.red[600]}>
          Delete File Upload
        </Button>
      ),
      className: clsx(classes.dropdownOption, classes.removeOption),
    },
  ];

  const linksOptions: DropdownOptionType[] = Object.entries(processingOutput)
    .filter(([, value]) => value !== null)
    .map(([key, value]) => ({
      value: value as string,
      onOptionClick: async (): Promise<void> => {
        const link = await getOutputDownloadUrl(id, value as string);
        downloadByUrl(link);
      },
      label: (
        <Button type="text" leftIconProp={<Download />}>
          {convertLinkName(key)}
        </Button>
      ),
      className: classes.dropdownOption,
    }));

  const isLinksDropdownDisabled = !isActionView && linksOptions.length === 0;

  return (
    <Dropdown
      name="More"
      options={isActionView ? dropdownOptions : linksOptions}
      optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
      optionsWidth="22rem"
      closedElement={
        <Button
          className={clsx(
            classes.dropdownOpenedBtn,
            classes.dropdownButton,
            isLinksDropdownDisabled && classes.disabledButton,
          )}
          color="primary"
          type="text"
          rightIconProp={<ChevronDown />}
          disabled={isLinksDropdownDisabled}
        >
          {isActionView ? 'MORE OPTIONS' : 'MORE LINKS'}
        </Button>
      }
      openedElement={
        <Button color="primary" className={classes.dropdownButton} rightIconProp={<ChevronDown />}>
          {isActionView ? 'MORE OPTIONS' : 'MORE LINKS'}
        </Button>
      }
    />
  );
});

export default MoreDropdown;
